import type { UserContract } from './contract';

// Frontend display role (simplified)
export type UserRole = 'learner' | 'org_admin' | 'super_admin';

// Backend enum types (matching C# enums)
export type PlatformRole = 'Learner' | 'OrgManager' | 'OrgOwner' | 'SuperAdmin';
export type UserType = 'B2C' | 'B2B';
export type EmploymentStatus = 'Active' | 'Inactive' | 'OnLeave' | 'Terminated';

// Full user entity from backend
export interface User {
  id: number;
  oktaId: string;
  organizationId: number | null;
  organizationName?: string;
  organizationStatus?: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string | null;
  department: string | null;
  location: string | null;
  phone: string | null;
  phoneCountryCode: string | null;
  userType: UserType;
  platformRole: PlatformRole;
  employmentStatus: EmploymentStatus;
  onboardingCompletedAt?: string | null;
  createdAt: string;
  updatedAt: string | null;
  lastLoginAt: string | null;
  salesforceContactId?: string | null;
  beaconUserId?: string | null;
}

/** Response of GET /api/organizations/{orgId}/users/{id}: the user plus the contracts they are on. */
export interface OrgUserDetail extends User {
  contracts: UserContract[];
}

// Response from POST /api/users — includes the created user and a Beacon provisioning flag
export interface CreateUserResponse {
  user: User;
  beaconProvisioned: boolean;
}

// Create user DTO
// Note: oktaId is auto-generated by the backend via Okta API, not provided by the client
export interface CreateUser {
  organizationId?: number | null;
  email: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  department?: string | null;
  location?: string | null;
  userType?: UserType;
  platformRole?: PlatformRole;
  salesforceContactId?: string | null;
  beaconUserId?: string | null;
  employmentStatus?: EmploymentStatus;
}

// Update user DTO
export interface UpdateUser {
  organizationId?: number | null;
  firstName?: string;
  lastName?: string;
  title?: string | null;
  department?: string | null;
  location?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
  userType?: UserType;
  platformRole?: PlatformRole;
  salesforceContactId?: string | null;
  beaconUserId?: string | null;
  employmentStatus?: EmploymentStatus;
}

// Query parameters for user list
export interface UserQuery {
  page?: number;
  limit?: number;
  search?: string;
  organizationId?: number;
  platformRole?: PlatformRole;
  employmentStatus?: EmploymentStatus;
  userType?: UserType;
  /** Org users endpoint only: leave out users already on this contract (BPE-821). */
  excludeContractId?: number;
  sortBy?: 'email' | 'firstName' | 'lastName' | 'createdAt' | 'lastLoginAt';
  sortDesc?: boolean;
}

// Re-export PagedResult from catalog for backwards compatibility
export type { PagedResult } from './catalog';

// Frontend profile interface (used in stores/views)
export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
  organizationId?: string;
  jurisdictions: string[];
  avatarUrl?: string;
  role: UserRole;
  onboardingCompletedAt?: string | null;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  emailNotifications: boolean;
  timezone: string;
}

// Helper functions

// Canonical role vocabulary (BPE-190).
//
// These four strings are the ONLY names a platform role may be shown under.
// Deprecated synonyms — bare "Admin", "Owner" or "Manager", "Organization
// Owner", "Organization Manager", "Org Manager", "Org Admin (Owner)" — must
// not appear in the UI.
//
// Note the OrgManager identifier is shown as "Org Admin": the backend enum
// name and the display name deliberately differ here. Display names are
// separate from the API identifiers, which stay SuperAdmin / OrgOwner /
// OrgManager / Learner on the wire regardless of what we call them on screen.
//
// Add a role here and it flows to every label, tag and role selector at once.
const PLATFORM_ROLE_LABELS: Record<PlatformRole, string> = {
  SuperAdmin: 'Super Admin',
  OrgOwner: 'Org Owner',
  OrgManager: 'Org Admin',
  Learner: 'Learner',
};

// Role tags are neutral and told apart by icon, like delivery tags: a role is
// neither a state (semantic variants) nor a content type (the palette variants,
// which belong to CLE, Skills, Pathway, …). Colouring roles made Super Admin and
// Org Owner identical and Org Admin read as a Pathway. See UX-Specifications.md,
// "Role tags".
const PLATFORM_ROLE_ICONS: Record<PlatformRole, string> = {
  SuperAdmin: 'admin_panel_settings',
  OrgOwner: 'domain',
  OrgManager: 'manage_accounts',
  Learner: 'person',
};

// Roles reach us as PlatformRole from typed API responses, but older screens
// pass loosely-typed strings and occasional snake_case fallbacks ('learner',
// 'org_owner'). Normalise rather than carrying an alias table per call site.
function normalizePlatformRole(role: PlatformRole | string | null | undefined): PlatformRole {
  const key = String(role ?? '').replace(/_/g, '').toLowerCase();
  const match = (Object.keys(PLATFORM_ROLE_LABELS) as PlatformRole[]).find(
    (r) => r.toLowerCase() === key
  );
  return match ?? 'Learner';
}

// Ordered for role selectors — most privileged first, matching how the role
// dropdowns have always been ordered.
export const PLATFORM_ROLE_OPTIONS: readonly { value: PlatformRole; label: string }[] = (
  ['SuperAdmin', 'OrgOwner', 'OrgManager', 'Learner'] as const
).map((value) => ({ value, label: PLATFORM_ROLE_LABELS[value] }));

// The org-admin tier, for the selectors that only promote within an organization.
export const ORG_ADMIN_ROLE_OPTIONS: readonly { value: PlatformRole; label: string }[] =
  PLATFORM_ROLE_OPTIONS.filter((o) => o.value === 'OrgOwner' || o.value === 'OrgManager');

export function getPlatformRoleLabel(role: PlatformRole | string | null | undefined): string {
  return PLATFORM_ROLE_LABELS[normalizePlatformRole(role)];
}

export function getPlatformRoleIcon(role: PlatformRole | string | null | undefined): string {
  return PLATFORM_ROLE_ICONS[normalizePlatformRole(role)];
}

// Employment status → bb-tag semantic variant: the one mapping every screen
// uses. Five local copies used to drift (Inactive red on one screen, grey on
// another; On Leave in Microlearning's pumpkin), and one keyed on 'active'
// while the API sends 'Active', so it matched nothing. Four states, four
// distinct variants — no two statuses read the same.
const EMPLOYMENT_STATUS_VARIANTS: Record<EmploymentStatus, string> = {
  Active: '-success',
  OnLeave: '-warning',
  Inactive: '-neutral',
  Terminated: '-danger',
};

/** Accepts the API's 'OnLeave' and older spellings ('on_leave', 'active'). */
export function getEmploymentStatusVariant(status: EmploymentStatus | string | null | undefined): string {
  const key = String(status ?? '').replace(/[_\s-]/g, '').toLowerCase();
  const match = (Object.keys(EMPLOYMENT_STATUS_VARIANTS) as EmploymentStatus[]).find(
    (s) => s.toLowerCase() === key
  );
  return match ? EMPLOYMENT_STATUS_VARIANTS[match] : '-neutral';
}

export function getEmploymentStatusLabel(status: EmploymentStatus): string {
  switch (status) {
    case 'Active':
      return 'Active';
    case 'Inactive':
      return 'Inactive';
    case 'OnLeave':
      return 'On Leave';
    case 'Terminated':
      return 'Terminated';
    default:
      return status;
  }
}

export function mapPlatformRoleToUserRole(platformRole: PlatformRole): UserRole {
  switch (platformRole) {
    case 'SuperAdmin':
      return 'super_admin';
    case 'OrgOwner':
    case 'OrgManager':
      return 'org_admin';
    default:
      return 'learner';
  }
}

// Salesforce Contact
export interface SalesforceContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  accountName: string | null;
}

/**
 * Which system holds the identity a row collided with (BPE-187).
 *
 * Null for every other outcome, including a failure to *verify* an ID — "Beacon
 * could not be reached" is an outage to retry, not a collision to take to support.
 */
export type IdentityConflictSource = 'Okta' | 'Beacon' | 'IdentityHub';

// CSV Import types
export interface UserCsvImportRowResult {
  rowNumber: number;
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  salesforceContactId: string | null;
  status: 'created' | 'updated' | 'skipped' | 'error';
  reason: string | null;
  conflictSource: IdentityConflictSource | null;
}

export interface UserCsvImportResult {
  totalRows: number;
  usersCreated: number;
  /** Existing users backfilled with a Salesforce Contact ID supplied by the CSV. */
  usersUpdated: number;
  usersSkipped: number;
  rowErrors: number;
  rows: UserCsvImportRowResult[];
}

// Org-admin CSV Import types (includes seat info and group creation)
export interface OrgUserCsvImportRowResult {
  rowNumber: number;
  email: string;
  firstName: string;
  lastName: string;
  groupName: string | null;
  status: 'created' | 'skipped' | 'error';
  reason: string | null;
  conflictSource: IdentityConflictSource | null;
}

export interface OrgUserCsvImportResult {
  totalRows: number;
  usersCreated: number;
  usersSkipped: number;
  rowErrors: number;
  groupsCreated: number;
  rows: OrgUserCsvImportRowResult[];
}

// BPE-188: Super Admin Okta lookup.
// GET /api/users/okta-lookup?email= answers "can this address be made a Super
// Admin, and who is it?" — the creation form calls it before submitting so the
// admin sees the matched person and gets a rejection inline.

/**
 * Why an address cannot be made a Super Admin.
 *
 * `account_setup_incomplete` is deliberately separate from `account_not_usable`:
 * only the former resolves on its own, so the copy must say "wait and retry"
 * rather than sending the admin to Okta — or worse, implying the address is
 * wrong and inviting them to create a second Okta account.
 */
export type SuperAdminLookupReason =
  | 'domain_not_allowed'
  | 'not_found_in_okta'
  | 'account_setup_incomplete'
  | 'account_not_usable';

export interface SuperAdminLookupResult {
  /** True only when the address is on an approved domain AND backed by an enabled Okta account. */
  matched: boolean;

  /** First name from the matched Okta profile. Present only when `matched`. */
  firstName: string | null;

  /** Last name from the matched Okta profile. Present only when `matched`. */
  lastName: string | null;

  /** Rejection reason. Null when `matched`. */
  reason: SuperAdminLookupReason | null;

  /** Approved domains, sent only with `domain_not_allowed` so the form can name them. */
  allowedDomains: string[] | null;
}
